import { useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react'
import { useEffect, type ReactNode } from 'react'
import { apiUpload } from './api'
import { cleanEditorHtml, editorExtensions } from './editorExtensions'

type Props = {
  value: string
  onChange: (html: string) => void
}

const SITE_HOST = 'theitgirlguide.com'

export default function PostEditor({ value, onChange }: Props) {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: editorExtensions(),
    content: value || '<p></p>',
    editorProps: {
      attributes: {
        class: 'admin-editor-body min-h-[280px] px-4 py-3 outline-none',
      },
      handleDrop: (_view, event) => {
        const file = event.dataTransfer?.files?.[0]
        if (!file?.type.startsWith('image/')) return false
        event.preventDefault()
        void addImage(file)
        return true
      },
      handlePaste: (_view, event) => {
        const file = event.clipboardData?.files?.[0]
        if (!file?.type.startsWith('image/')) return false
        event.preventDefault()
        void addImage(file)
        return true
      },
    },
    onUpdate: ({ editor: next }) => onChange(cleanEditorHtml(next.getHTML())),
  })

  useEffect(() => {
    if (!editor) return
    const current = cleanEditorHtml(editor.getHTML())
    if (value && value !== current && !editor.isFocused) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  async function addImage(file?: File) {
    if (!editor) return
    const picked = file ?? (await pickFile())
    if (!picked) return
    const alt = window.prompt('Alt text for this image', picked.name.replace(/\.[^.]+$/, '')) || ''
    const uploaded = await apiUpload(picked, 'inline')
    editor.chain().focus().setImage({ src: uploaded.url, alt }).run()
  }

  if (!editor) return <div className="min-h-[320px] border border-rule bg-sand" />

  return (
    <div className="border border-rule">
      <Toolbar editor={editor} onImage={() => void addImage()} />
      <EditorContent editor={editor} />
    </div>
  )
}

const BLOCKS = [
  { value: 'p', label: 'Paragraph' },
  { value: '1', label: 'Heading 1' },
  { value: '2', label: 'Heading 2' },
  { value: '3', label: 'Heading 3' },
  { value: '4', label: 'Heading 4' },
  { value: '5', label: 'Heading 5' },
  { value: '6', label: 'Heading 6' },
] as const

function Toolbar({ editor, onImage }: { editor: Editor; onImage: () => void }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      block: ([1, 2, 3, 4, 5, 6].find((level) => e.isActive('heading', { level })) ?? 'p').toString(),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      left: e.isActive({ textAlign: 'left' }),
      center: e.isActive({ textAlign: 'center' }),
      right: e.isActive({ textAlign: 'right' }),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      blockquote: e.isActive('blockquote'),
      link: e.isActive('link'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  })

  function setBlock(next: string) {
    const chain = editor.chain().focus()
    if (next === 'p') chain.setParagraph().run()
    else chain.setHeading({ level: Number(next) as 1 | 2 | 3 | 4 | 5 | 6 }).run()
  }

  function setLink() {
    const previous = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', previous || 'https://')
    if (url === null) return
    const href = url.trim()
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    const external = /^https?:\/\//i.test(href) && !new RegExp(`^https?://(www\\.)?${SITE_HOST.replace('.', '\\.')}`, 'i').test(href)
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink(external ? { href, target: '_blank', rel: 'noopener noreferrer' } : { href, target: null, rel: null })
      .run()
  }

  // Headings 1 is the page title, so it is only listed when an old post already uses it.
  const blocks = BLOCKS.filter((item) => item.value !== '1' || state.block === '1')

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-rule bg-sand-2 px-1.5 py-1"
    >
      <Btn label="Undo" disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
        <Icon d="M9 14 4 9l5-5 M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
      </Btn>
      <Btn label="Redo" disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
        <Icon d="m15 14 5-5-5-5 M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
      </Btn>
      <Sep />
      <select
        aria-label="Text style"
        value={state.block}
        onChange={(event) => setBlock(event.target.value)}
        className="h-8 border border-rule bg-paper px-2 text-[13px] text-ink outline-none focus:border-ink"
      >
        {blocks.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
      <Sep />
      <Btn label="Bold" active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Icon d="M6 12h9a4 4 0 0 1 0 8H6V4h8a4 4 0 0 1 0 8" />
      </Btn>
      <Btn label="Italic" active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Icon d="M19 4h-9 M14 20H5 M15 4 9 20" />
      </Btn>
      <Btn label="Underline" active={state.underline} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <Icon d="M6 4v6a6 6 0 0 0 12 0V4 M4 20h16" />
      </Btn>
      <Btn label="Strikethrough" active={state.strike} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Icon d="M16 4H9a3 3 0 0 0-2.83 4 M14 12a4 4 0 0 1 0 8H6 M4 12h16" />
      </Btn>
      <Sep />
      <Btn label="Align left" active={state.left} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        <Icon d="M21 6H3 M15 12H3 M17 18H3" />
      </Btn>
      <Btn label="Align center" active={state.center} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        <Icon d="M21 6H3 M17 12H7 M19 18H5" />
      </Btn>
      <Btn label="Align right" active={state.right} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        <Icon d="M21 6H3 M21 12H9 M21 18H7" />
      </Btn>
      <Sep />
      <Btn label="Bulleted list" active={state.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <Icon d="M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01" />
      </Btn>
      <Btn label="Numbered list" active={state.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <Icon d="M10 6h11 M10 12h11 M10 18h11 M4 6h1v4 M4 10h2 M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
      </Btn>
      <Btn label="Quote" active={state.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Icon d="M3 21c3 0 7-1 7-8V5c0-1.25-.76-2-2-2H4c-1.25 0-2 .75-2 1.97V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .01-1 1.03V20c0 1 0 1 1 1z M15 21c3 0 7-1 7-8V5c0-1.25-.76-2-2-2h-4c-1.25 0-2 .75-2 1.97V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
      </Btn>
      <Sep />
      <Btn label="Link" active={state.link} onClick={setLink}>
        <Icon d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71 M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </Btn>
      <Btn label="Image" onClick={onImage}>
        <Icon d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M9 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z m12 4-3.09-3.09a2 2 0 0 0-2.82 0L6 21" />
      </Btn>
      <Btn label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Icon d="M3 12h18" />
      </Btn>
      <Sep />
      <Btn label="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        <Icon d="M4 7V4h16v3 M5 20h6 M13 4 8 20 m7-5 5 5 m0-5-5 5" />
      </Btn>
    </div>
  )
}

function Btn({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={`grid h-8 w-8 place-items-center rounded-sm disabled:opacity-35 ${
        active ? 'bg-ink text-white' : 'text-ink/75 hover:bg-sand hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

function Sep() {
  return <span aria-hidden className="mx-1 h-5 w-px bg-rule" />
}

function Icon({ d }: { d: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {d.split(' M').map((part, index) => (
        <path key={index} d={index === 0 ? part : `M${part}`} />
      ))}
    </svg>
  )
}

function pickFile() {
  return new Promise<File | undefined>((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/jpeg,image/png,image/webp,image/gif,image/avif'
    input.onchange = () => resolve(input.files?.[0])
    input.click()
  })
}
